import { z } from 'zod';
import { parseWebEnv, type AppEnvT, type FirebaseEnvT } from '@lotushealth/web_kit/config';

/**
 * Application environment types
 */
export type AppEnv = AppEnvT;

/**
 * Log level types (ordered from most to least verbose)
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type FirebaseEnv = FirebaseEnvT;

export type AnalyticsEnv = Readonly<{
  /**
   * Segment write key used by the browser SDK.
   * If undefined, Segment should not be initialized.
   */
  segmentWriteKey?: string;
}>;

export type SentryEnv = Readonly<{
  /**
   * Sentry DSN for error reporting.
   * If undefined, Sentry should not be initialized.
   */
  dsn?: string;
  /**
   * Sentry environment.
   *
   * If undefined, Sentry will use the "local" environment.
   */
  environment?: string;
}>;

const parsedEnv = parseWebEnv(import.meta.env, {
  extra: {
    /** Minimum log level for the logger */
    VITE_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),

    /**
     * Analytics (optional).
     * We keep these optional so environments can roll out incrementally.
     */
    VITE_SEGMENT_WRITE_KEY: z.string().min(1).optional(),

    /**
     * Sentry (optional).
     * DSN for error reporting. If not provided, Sentry will not be initialized.
     */
    VITE_SENTRY_DSN: z.url().optional(),
  },
});

/**
 * Validated environment configuration.
 * All environment variable access should go through this object.
 */
export const ENV = {
  /** Current application environment */
  appEnv: parsedEnv.appEnv,

  /** Minimum log level */
  logLevel: parsedEnv.extra.VITE_LOG_LEVEL,

  /** Base URL for API requests (undefined if not configured) */
  apiBaseUrl: parsedEnv.apiBaseUrl,

  analytics: {
    segmentWriteKey: parsedEnv.extra.VITE_SEGMENT_WRITE_KEY,
  } satisfies AnalyticsEnv,

  sentry: {
    dsn: parsedEnv.extra.VITE_SENTRY_DSN,
  } satisfies SentryEnv,

  /**
   * Use `ENV.firebase` instead of reading `import.meta.env` directly.
   */
  firebase: parsedEnv.firebase,

  /** Whether app is running in development mode */
  isDevelopment: parsedEnv.isDevelopment,

  /** Whether app is running in production mode */
  isProduction: parsedEnv.isProduction,
} as const;

export type Env = typeof ENV;
